import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import {
  IListElement,
  IDateElements,
  ISkyboxUser,
  IRole,
  IBuyerTag,
  IListerTag,
  ITicketbashBroker,
  ISkyboxVendor,
} from "models/interfaces";
import { FilterOptionsService as SkyBoxFilterOptionService } from "services/SkyboxServices/filters/filterOptions.service";
import { FilterOptionsService as StubHubFilterOptionServices } from "services/StubHubServices/filters/filterOptions.service";

interface IFilterOptionService {
  getShowCategories(): Promise<any>;
  getBrokerTagsByReportId(args: any): Promise<any>;
  getBuyers(): Promise<any>;
  getEvents(): Promise<any>;
  getVenues(): Promise<any>;
  getDateRange(): Promise<any>;
  getCards(): Promise<any>;
  getAlerts(): Promise<any>;
  getCurrencies(): Promise<any>;
  getReports(args: any): Promise<any>;
  getRecipients(): Promise<any>;
  getSkyBoxUsers(): Promise<any>;
  getSkyBoxVendors(): Promise<any>;
  getRoles(): Promise<any>;
  getSplitTypes(): Promise<any>;
  getBuyerTypes(): Promise<any>;
  getListerTypes(): Promise<any>;
  getTicketbashBrokerList(): Promise<any>;
  getSaleCategories(): Promise<any>;
  getCustomerClassifications(): Promise<any>;
  getStates(): Promise<any>;
  getSeatIntelligenceDetails(): Promise<any>;
  getInventoryCancellationTypes(): Promise<any>;
  getScrapingStatusDetails(): Promise<any>;
  getBrokerUserList(): Promise<any>;
  getBuyerUserList(): Promise<any>;
  getListerUserList(): Promise<any>;
  getShippers(): Promise<any>;
  getSeatMapCreatorUserList(): Promise<any>;
  getCheckerUserList(): Promise<any>;
  getVenuesDatePresets(): Promise<any>;
  getPaymentStatusList(): Promise<any>;
  getAgingPeriodList(): Promise<any>;
  getFulfillmentStatusList(): Promise<any>;
  getVenueScrapingTypes(): Promise<any>;
}

let initialState: {
  brokers: IListElement[];
  buyers: IListElement[];
  shippers: IListElement[];
  events: IListElement[];
  venues: IListElement[];
  dateRange: IDateElements[];
  cards: IListElement[];
  alerts: IListElement[];
  currencies: IListElement[];
  reports: IListElement[];
  recipients: IListElement[];
  skyboxUsers: ISkyboxUser[];
  skyboxVendors: ISkyboxVendor[];
  roles: IRole[];
  splitTypes: IListElement[];
  buyerTypes: IBuyerTag[];
  listerTypes: IListerTag[];
  ticketbashBrokers: ITicketbashBroker[];
  saleCategories: IListElement[];
  customerClassifications: IListElement[];
  states: IListElement[];
  inventoryCancellationTypes: IListElement[];
  seatIntelligenceData: IListElement[];
  scrapingStatusData: IListElement[];
  brokerUsers: IListElement[];
  listerUsers: IListElement[];
  buyerUsers: IListElement[];
  seatMapCreatorUsers: IListElement[];
  checkerUsers: IListElement[];
  venueDatePresets: IListElement[];
  stockTypes: IListElement[];
  paymentStatusTypes: IListElement[];
  agingPeriodTypes: IListElement[];
  fulfillmentStatusTypes: IListElement[];
  showCategories: IListElement[];
  venueScrapingTypes: IListElement[];
} = {
  brokers: [],
  buyers: [],
  shippers: [],
  events: [],
  venues: [],
  dateRange: [],
  cards: [],
  alerts: [],
  currencies: [],
  reports: [],
  recipients: [],
  skyboxUsers: [],
  skyboxVendors: [],
  roles: [],
  splitTypes: [],
  buyerTypes: [],
  listerTypes: [],
  ticketbashBrokers: [],
  saleCategories: [],
  customerClassifications: [],
  states: [],
  inventoryCancellationTypes: [],
  seatIntelligenceData: [],
  scrapingStatusData: [],
  brokerUsers: [],
  listerUsers: [],
  buyerUsers: [],
  seatMapCreatorUsers: [],
  checkerUsers: [],
  venueDatePresets: [],
  stockTypes: [],
  paymentStatusTypes: [],
  agingPeriodTypes: [],
  fulfillmentStatusTypes: [],
  showCategories: [],
  venueScrapingTypes: []
};

class FilterOptionServiceFactory {
  private static skyBoxService: IFilterOptionService 
  private static stubHubService: IFilterOptionService

  static getService(environment: 'stubHub' | 'skyBox'): IFilterOptionService {
    if (environment === 'stubHub') {
      if (!this.stubHubService) {
        this.stubHubService = new StubHubFilterOptionServices();
      }
      return this.stubHubService;
    } else {
      if (!this.skyBoxService) {
        this.skyBoxService = new SkyBoxFilterOptionService();
      }
      return this.skyBoxService;
    }
  }
}

const getShowCategories = createAsyncThunk(
  "filters/getShowCategories",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getShowCategories();
    return response.data.data.show_categories;
  }
);

const getBrokerFilterData = createAsyncThunk(
  "filters/getBrokerFilterData",
  async ({ current_report_id, env }: { current_report_id: number; env: 'stubHub' | 'skyBox' }) => {
    const response = await FilterOptionServiceFactory.getService(env).getBrokerTagsByReportId({
      current_report_id,
    });
    return response.data.data.brokers;
  }
);

const getBuyerFilterData = createAsyncThunk(
  "filters/getBuyerFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getBuyers();
    return response.data.data.buyers;
  }
);

const getEventFilterData = createAsyncThunk(
  "filters/getEventFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getEvents();
    return response.data.data.events;
  }
);

const getVenueFilterData = createAsyncThunk(
  "filters/getVenueFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getVenues();
    return response.data.data.venues;
  }
);

const getDateRangeFilterData = createAsyncThunk(
  "filters/getDateRangeFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getDateRange();
    return response.data.data.dates;
  }
);

const getCardFilterData = createAsyncThunk(
  "filters/getCardFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getCards();
    return response.data.data.cards;
  }
);

const getAlertFilterData = createAsyncThunk(
  "filters/getAlertFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getAlerts();
    return response.data.data.alerts;
  }
);

const getCurrencyFilterData = createAsyncThunk(
  "filters/getCurrencyFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getCurrencies();
    return response.data.data.currencies;
  }
);

const getReportFilterData = createAsyncThunk(
  "filters/getReportFilterData",
  async ({ current_report_id, env }: { current_report_id: number; env: 'skyBox' | 'stubHub' }) => {
    const response = await FilterOptionServiceFactory.getService(env).getReports({
      current_report_id,
    });
    return response.data.data.reports;
  }
);

const getRecipientFilterData = createAsyncThunk(
  "filters/getRecipientFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getRecipients();
    return response.data.data.recipients;
  }
);

const getSkyBoxUserData = createAsyncThunk(
  "filters/getSkyBoxUserData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getSkyBoxUsers();
    return response.data.data.allSkyboxUsers;
  }
);

const getSkyBoxVendorData = createAsyncThunk(
  "filters/getVendorsData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getSkyBoxVendors();
    return response.data.data.allSkyboxVendors;
  }
);

const getRoleData = createAsyncThunk("filters/getRoleData", async (args:any) => {
  const response = await FilterOptionServiceFactory.getService(args.env).getRoles();
  return response.data.data.roles;
});

const getSplitTypeData = createAsyncThunk(
  "filters/getSplitTypeData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getSplitTypes();
    return response.data.data.splitTypes.filter(
      (each:any) => each.label.toLowerCase() !== "custom"
    );
  }
);

const getBuyerTagData = createAsyncThunk(
  "filters/getBuyerTagData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getBuyerTypes();
    return response.data.data.buyerTags;
  }
);

const getListerTagData = createAsyncThunk(
  "filters/getListerTagData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getListerTypes();
    return response.data.data.listerTags;
  }
);

const getTicketbashBrokerData = createAsyncThunk(
  "filters/getTicketbashBrokerData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getTicketbashBrokerList();
    return response.data.data.ticketbashBrokers;
  }
);

const getSaleCategoryFilterData = createAsyncThunk(
  "filters/getSaleCategoryFilterData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getSaleCategories();
    return response.data.data.saleCategories;
  }
);

const getCustomerClassificationFilterData = createAsyncThunk(
  "filters/getCustomerClassificationFilterData",
  async (args:any) => {
    const response =
      await FilterOptionServiceFactory.getService(args.env).getCustomerClassifications();
    return response.data.data.customerClassifications;
  }
);

const getAllStatesData = createAsyncThunk(
  "filters/getAllStatesData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getStates();
    return response.data.data.states;
  }
);

const getAllInventoryCancellationTypesData = createAsyncThunk(
  "filters/getAllInventoryCancellationTypesData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getInventoryCancellationTypes();
    return response.data.data.inventoryCancellationTypes;  
  }
)

const getSeatIntelligenceData = createAsyncThunk(
  "filters/getSeatIntelligenceData",
  async (args:any) => {
    const response =
      await FilterOptionServiceFactory.getService(args.env).getSeatIntelligenceDetails();
    return response.data.data.seatIntelligenceData;
  }
);

const getScrapingStatusData = createAsyncThunk(
  "filters/getScrapingStatusData",
  async (args:any) => {
    const response =
      await FilterOptionServiceFactory.getService(args.env).getScrapingStatusDetails();
    return response.data.data.scrapingStatusData;
  }
);

const getBrokerUserData = createAsyncThunk(
  "filters/getBrokerUserData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getBrokerUserList();
    return response.data.data.brokerUserList;
  }
);

const getBuyerUserData = createAsyncThunk(
  "filters/getBuyerUserData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getBuyerUserList();
    return response.data.data.buyerUserList;
  }
);

const getListerUserData = createAsyncThunk(
  "filters/getListerUserData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getListerUserList();
    return response.data.data.listerUserList;
  }
);

const getShipperUserData = createAsyncThunk(
  "filters/getShipperUserData",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getShippers();
    return response.data.data.shippers;
  }
);

const getSeatMapCreatorUserData = createAsyncThunk(
  "filters/getSeatMapCreatorUserData",
  async (args:any) => {
    const response =
      await FilterOptionServiceFactory.getService(args.env).getSeatMapCreatorUserList();
    return response.data.data.seatMapCreatorUserList;
  }
);

const getCheckerUserData = createAsyncThunk(
  'filters/getCheckerUserData',
  async (args: any) => {
    const response = await FilterOptionServiceFactory.getService(
      args.env,
    ).getCheckerUserList();
    return response.data.data.checkerUserList;
  },
);

const getVenuesDatePresets = createAsyncThunk(
  "filters/getVenuesDatePresets",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getVenuesDatePresets();
    return response.data.data.newVenues;
  }
);

const getPaymentStatusList = createAsyncThunk(
  "filters/getPaymentStatusList",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getPaymentStatusList();
    return response.data.data.paymentStatus;
  }
);

const getAgingPeriodList = createAsyncThunk(
  "filters/getAgingPeriodList",
  async (args:any) => {
    const response = await FilterOptionServiceFactory.getService(args.env).getAgingPeriodList();
    return response.data.data.agingPeriods;
  }
);

const getFulfillmentStatusList = createAsyncThunk(
  "filters/getFulfillmentStatusList",
  async (args:any) => {
    const response =
      await FilterOptionServiceFactory.getService(args.env).getFulfillmentStatusList();
    return response.data.data.fulfillmentStatuses;
  }
);

const getVenueScrapingTypes = createAsyncThunk(
  "filters/getVenueScrapingTypes",
  async (args:any) => {
    const response =
      await FilterOptionServiceFactory.getService(args.env).getVenueScrapingTypes();
    return response.data.data.venue_scraping_types;
  }
);

export const filterSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getShowCategories.fulfilled, (state, action) => {
      state.showCategories = action.payload;
    });

    builder.addCase(getBrokerFilterData.fulfilled, (state, action) => {
      state.brokers = action.payload;
    });

    builder.addCase(getBuyerFilterData.fulfilled, (state, action) => {
      state.buyers = action.payload;
    });

    builder.addCase(getShipperUserData.fulfilled, (state, action) => {
      state.shippers = action.payload;
    });

    builder.addCase(getEventFilterData.fulfilled, (state, action) => {
      state.events = action.payload;
    });

    builder.addCase(getVenueFilterData.fulfilled, (state, action) => {
      state.venues = action.payload;
    });

    builder.addCase(getDateRangeFilterData.fulfilled, (state, action) => {
      state.dateRange = action.payload;
    });

    builder.addCase(getCardFilterData.fulfilled, (state, action) => {
      state.cards = action.payload;
    });

    builder.addCase(getAlertFilterData.fulfilled, (state, action) => {
      state.alerts = action.payload;
    });

    builder.addCase(getCurrencyFilterData.fulfilled, (state, action) => {
      state.currencies = action.payload;
    });

    builder.addCase(getReportFilterData.fulfilled, (state, action) => {
      state.reports = action.payload;
    });

    builder.addCase(getRecipientFilterData.fulfilled, (state, action) => {
      state.recipients = action.payload;
    });

    builder.addCase(getSkyBoxUserData.fulfilled, (state, action) => {
      state.skyboxUsers = action.payload;
    });

    builder.addCase(getSkyBoxVendorData.fulfilled, (state, action) => {
      state.skyboxVendors = action.payload;
    });

    builder.addCase(getRoleData.fulfilled, (state, action) => {
      state.roles = action.payload;
    });

    builder.addCase(getSplitTypeData.fulfilled, (state, action) => {
      state.splitTypes = action.payload;
    });

    builder.addCase(getBuyerTagData.fulfilled, (state, action) => {
      state.buyerTypes = action.payload;
    });

    builder.addCase(getListerTagData.fulfilled, (state, action) => {
      state.listerTypes = action.payload;
    });

    builder.addCase(getTicketbashBrokerData.fulfilled, (state, action) => {
      state.ticketbashBrokers = action.payload;
    });

    builder.addCase(getSaleCategoryFilterData.fulfilled, (state, action) => {
      state.saleCategories = action.payload;
    });

    builder.addCase(
      getCustomerClassificationFilterData.fulfilled,
      (state, action) => {
        state.customerClassifications = action.payload;
      }
    );

    builder.addCase(getAllStatesData.fulfilled, (state, action) => {
      state.states = action.payload;
    });

    builder.addCase(getAllInventoryCancellationTypesData.fulfilled, (state, action) => {
      state.inventoryCancellationTypes = action.payload;
    })

    builder.addCase(getSeatIntelligenceData.fulfilled, (state, action) => {
      state.seatIntelligenceData = action.payload;
    });

    builder.addCase(getScrapingStatusData.fulfilled, (state, action) => {
      state.scrapingStatusData = action.payload;
    });

    builder.addCase(getBrokerUserData.fulfilled, (state, action) => {
      state.brokerUsers = action.payload;
    });

    builder.addCase(getListerUserData.fulfilled, (state, action) => {
      state.listerUsers = action.payload;
    });

    builder.addCase(getBuyerUserData.fulfilled, (state, action) => {
      state.buyerUsers = action.payload;
    });

    builder.addCase(getSeatMapCreatorUserData.fulfilled, (state, action) => {
      state.seatMapCreatorUsers = action.payload;
    });

    builder.addCase(getCheckerUserData.fulfilled, (state, action) => {
      state.checkerUsers = action.payload;
    });

    builder.addCase(getVenuesDatePresets.fulfilled, (state, action) => {
      state.venueDatePresets = action.payload;
    });

    builder.addCase(getPaymentStatusList.fulfilled, (state, action) => {
      state.paymentStatusTypes = action.payload;
    });

    builder.addCase(getAgingPeriodList.fulfilled, (state, action) => {
      state.agingPeriodTypes = action.payload;
    });

    builder.addCase(getFulfillmentStatusList.fulfilled, (state, action) => {
      state.fulfillmentStatusTypes = action.payload;
    });
    
    builder.addCase(getVenueScrapingTypes.fulfilled, (state, action) => {
      state.venueScrapingTypes = action.payload;
    });
  },
});

const brokers = (state: RootState) => state.filters.brokers;
const buyers = (state: RootState) => state.filters.buyers;
const shippers = (state: RootState) => state.filters.shippers;
const events = (state: RootState) => state.filters.events;
const venues = (state: RootState) => state.filters.venues;
const dateRange = (state: RootState) => state.filters.dateRange;
const cards = (state: RootState) => state.filters.cards;
const alerts = (state: RootState) => state.filters.alerts;
const currencies = (state: RootState) => state.filters.currencies;
const reports = (state: RootState) => state.filters.reports;
const recipients = (state: RootState) => state.filters.recipients;
const skyboxUsers = (state: RootState) => state.filters.skyboxUsers;
const skyboxVendors = (state: RootState) => state.filters.skyboxVendors;
const roles = (state: RootState) => state.filters.roles;
const splitTypes = (state: RootState) => state.filters.splitTypes;
const buyerTags = (state: RootState) => state.filters.buyerTypes;
const listerTags = (state: RootState) => state.filters.listerTypes;
const ticketbashBrokers = (state: RootState) => state.filters.ticketbashBrokers;
const saleCategories = (state: RootState) => state.filters.saleCategories;
const customerClassifications = (state: RootState) =>
  state.filters.customerClassifications;
const states = (state: RootState) => state.filters.states;
const inventoryCancellationTypes = (state: RootState) => state.filters.inventoryCancellationTypes;
const seatIntelligenceData = (state: RootState) =>
  state.filters.seatIntelligenceData;
const scrapingStatusData = (state: RootState) =>
  state.filters.scrapingStatusData;
const brokerUsers = (state: RootState) => state.filters.brokerUsers;
const listerUsers = (state: RootState) => state.filters.listerUsers;
const buyerUsers = (state: RootState) => state.filters.buyerUsers;
const seatMapCreatorUsers = (state: RootState) =>
  state.filters.seatMapCreatorUsers;
const checkerUsers = (state: RootState) => state.filters.checkerUsers;
const venueDatePresets = (state: RootState) => state.filters.venueDatePresets;
const paymentStatusTypes = (state: RootState) =>
  state.filters.paymentStatusTypes;
const agingPeriodTypes = (state: RootState) => state.filters.agingPeriodTypes;
const fulfillmentStatusTypes = (state: RootState) =>
  state.filters.fulfillmentStatusTypes;
const showCategories = (state: RootState) => state.filters.showCategories;
const venueScrapingTypes = (state: RootState) => state.filters.venueScrapingTypes;

export const actions = {
  getBrokerFilterData,
  getBuyerFilterData,
  getShipperUserData,
  getEventFilterData,
  getVenueFilterData,
  getDateRangeFilterData,
  getCardFilterData,
  getAlertFilterData,
  getCurrencyFilterData,
  getReportFilterData,
  getRecipientFilterData,
  getSkyBoxUserData,
  getSkyBoxVendorData,
  getRoleData,
  getSplitTypeData,
  getBuyerTagData,
  getListerTagData,
  getTicketbashBrokerData,
  getSaleCategoryFilterData,
  getCustomerClassificationFilterData,
  getAllStatesData,
  getAllInventoryCancellationTypesData,
  getSeatIntelligenceData,
  getScrapingStatusData,
  getBrokerUserData,
  getListerUserData,
  getBuyerUserData,
  getVenuesDatePresets,
  getPaymentStatusList,
  getAgingPeriodList,
  getSeatMapCreatorUserData,
  getCheckerUserData,
  getFulfillmentStatusList,
  getShowCategories,
  getVenueScrapingTypes
};
export const { reset } = filterSlice.actions;
export {
  brokers,
  buyers,
  shippers,
  events,
  venues,
  dateRange,
  cards,
  alerts,
  currencies,
  reports,
  recipients,
  skyboxUsers,
  skyboxVendors,
  roles,
  splitTypes,
  buyerTags,
  listerTags,
  ticketbashBrokers,
  saleCategories,
  customerClassifications,
  states,
  inventoryCancellationTypes,
  seatIntelligenceData,
  scrapingStatusData,
  brokerUsers,
  listerUsers,
  buyerUsers,
  venueDatePresets,
  paymentStatusTypes,
  agingPeriodTypes,
  seatMapCreatorUsers,
  checkerUsers,
  fulfillmentStatusTypes,
  showCategories,
  venueScrapingTypes
};
export default filterSlice.reducer;
